import {
  LDFlags,
} from '../constants'
import { LaunchDarkly } from '../launchDarkly'

import {
  CError,
  CLog,
} from '../consoleMixin'

import _Store from '../../store/store'
import { Debtor } from '../api/'
import {
  captureSentryEvent,
} from '../sentryMixin'

/**
 * @typedef {import('../api-v2/js-doc').V2MappingOptions} V2MappingOptions
 */

export class ApiVersionDelagator {
  /**
   * Checks to see if the client has the 'ingest-through-v2' flag enabled
   * @param {string} clientId - ID of the client
   * @returns {Promise<boolean>}
   */
  async #isIngestThroughV2EnabledForClient (clientId) {
    return await LaunchDarkly.isFlagEnabledForClient(clientId, LDFlags.ingestThroughV2)
  }

  async isV2ListingEnabled () {
    return await LaunchDarkly.isFlagEnabled(LDFlags.useV2InvoiceListing)
  }


  /**
   * Checks to see if the client has the 'use-v2' flag enabled
   * @param {string} clientId - ID of the client
   * @returns {Promise<boolean>}
   */
  async isIngestAlongsideV2EnabledForClient (clientId) {
    const featureFlag = LDFlags.useV2
    const value = await LaunchDarkly.isFlagEnabledForClient(clientId, featureFlag)
    CLog('Feature flag', featureFlag, 'has value of', value, 'for client id', clientId)

    if (!value) {
      CLog(`Feature flag ${featureFlag} is disabled for`, LaunchDarkly.context())
      return false
    }

    return value
  }

  /* eslint-disable max-len */
  /**
   * @template TRequestV1
   * @template TResponseV1
   *
   * @param {string} clientId - ID of the client
   * @param {TRequestV1} v1Request - V1 request body
   * @param {(request: TRequestV1) => Promise<TResponseV1>} v1FnWrapper - Function wrapper that does the call to V1 API
   * @param {(v2MappingOptions: V2MappingOptions, request: TRequestV1, response?: TResponseV1) => Promise<TResponseV1>} v2FnWrapper - Function wrapper that does the call to V2 API
   * @param {{failSilently?: boolean, operationName: string}} options - Configuration options
   * @returns {Promise<TResponseV1>}
   */
  /* eslint-enable max-len */

  async runWriteApiCalls (clientId, v1Request, v1FnWrapper, v2FnWrapper, options) {
    options.failSilently ??= true
    if (true) {
      return await v2FnWrapper({
        ingestThrough: true,
      }, v1Request)
    }
    else {
      const v1Response = await v1FnWrapper(v1Request)
      try {
        if (await this.isIngestAlongsideV2EnabledForClient(clientId)) {
          const v2Response = await v2FnWrapper({
            ingestThrough: false,
          }, v1Request, v1Response)
          if (LaunchDarkly.isFlagEnabled(LDFlags.useV2InvoiceListing)) {
            CLog('Listing is enabled. Will return V2 response')
            return v2Response
          }
        }
      }
      catch (error) {
        CError(error)
        try {
          captureSentryEvent(
            `V2 REQUEST: ${options.operationName}`,
            {
              clientID: clientId,
              url: error.config?.url,
              V1: {
                request: v1Request,
                response: v1Response,
              },
              V2: {
                response: error.response?.config,
              },
              details: error,
            }
          )
        }
        catch (error) {
          CError('Error on captureSentryEvent api-delegator', error)
        }

        if (!options.failSilently || LaunchDarkly.isFlagEnabled(LDFlags.useV2InvoiceListing)) {
          throw error
        }
      }
      return v1Response
    }
  }

  /* eslint-disable max-len */
  /**
   * @template TResponse
   *
   * @param {() => Promise<TResponse>} v1FnWrapper - Function wrapper that does the call to V1 API
   * @param {() => Promise<TResponse>} v2FnWrapper - Function wrapper that does the call to V2 API
   * @returns {Promise<TResponse>}
   */
  /* eslint-enable max-len */
  async runGetApiCalls (v1FnWrapper, v2FnWrapper) {
    if (await this.isV2ListingEnabled()) {
      return await v2FnWrapper()
    }
    else {
      return await v1FnWrapper()
    }
  }

  async getUnknownDebtorID () {
    if (await this.isV2ListingEnabled()) {
      return _Store.debtorNotFound.id
    }
    else {
      return await Debtor.getUnknownDebtorId()
    }
  }
}
